import React, { useEffect, useMemo, useState } from "react";
import "../StockInward.css";
import { getSalesEntries, getItems } from "../storage";
import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";

const BILLING_CATEGORY = "Billing";
const BILLING_PAYMENT_MODES = ["Any", "UPI", "Cash", "Credit", "Cheque", "Acc Transfer", "Razorpay", "Card"];

function normalizeBillPaymentMode(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "any") return "Any";
  if (raw === "upi") return "UPI";
  if (raw === "cash") return "Cash";
  if (raw === "cheque" || raw === "check") return "Cheque";
  if (raw === "acc transfer" || raw === "account transfer" || raw === "bank transfer") return "Acc Transfer";
  if (raw === "razorpay") return "Razorpay";
  if (raw === "card" || raw === "credit card" || raw === "debit card") return "Card";
  if (raw === "credit") return "Credit";
  return "Any";
}

function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const header = Object.keys(rows[0]).join(",");
  const body = rows
    .map((r) =>
      Object.keys(r)
        .map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");
  const csv = `${header}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function toMoneyCents(value) {
  return Math.max(0, Math.round((Number(value) || 0) * 100));
}

function distributeDiscountAcrossRows(itemAmounts, discountAmount) {
  const amounts = Array.isArray(itemAmounts) ? itemAmounts.map((value) => toMoneyCents(value)) : [];
  if (!amounts.length) return [];

  const totalAmountCents = amounts.reduce((sum, value) => sum + value, 0);
  const totalDiscountCents = Math.min(toMoneyCents(discountAmount), totalAmountCents);
  if (totalAmountCents <= 0 || totalDiscountCents <= 0) {
    return amounts.map(() => 0);
  }

  const baseShares = amounts.map((amount) => Math.floor((totalDiscountCents * amount) / totalAmountCents));
  let allocated = baseShares.reduce((sum, value) => sum + value, 0);
  let remainder = totalDiscountCents - allocated;

  if (remainder > 0) {
    const priority = amounts
      .map((amount, index) => ({
        index,
        fraction: (totalDiscountCents * amount) % totalAmountCents,
        capacity: amount - baseShares[index],
      }))
      .filter((entry) => entry.capacity > 0)
      .sort((a, b) => {
        if (b.fraction !== a.fraction) return b.fraction - a.fraction;
        if (b.capacity !== a.capacity) return b.capacity - a.capacity;
        return a.index - b.index;
      });

    for (let i = 0; i < priority.length && remainder > 0; i += 1) {
      baseShares[priority[i].index] += 1;
      remainder -= 1;
    }
  }

  return baseShares;
}

export default function BillingReport() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedSummaryItem, setSelectedSummaryItem] = useState("");
  const [entries, setEntries] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    customer: "",
    item: "",
    itemCode: "",
    itemGroup: "",
    payment: "any",
    paymentMode: "any",
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await getSalesEntries();
      setEntries(Array.isArray(list) ? list.filter((e) => e.category === BILLING_CATEGORY) : []);
      const itemList = await getItems();
      setItems(Array.isArray(itemList) ? itemList : []);
    } catch (err) {
      console.error(err);
      alert("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const billingItems = useMemo(
    () => (Array.isArray(items) ? items : []),
    [items]
  );

  const itemByName = useMemo(() => {
    const map = new Map();
    billingItems.forEach((it) => map.set(String(it.name || "").toLowerCase(), it));
    return map;
  }, [billingItems]);

  const itemByCode = useMemo(() => {
    const map = new Map();
    billingItems.forEach((it) => map.set(String(it.code || "").toLowerCase(), it));
    return map;
  }, [billingItems]);

  const itemRows = useMemo(() => {
    return entries.flatMap((en) => {
      const entryItems = Array.isArray(en.items) ? en.items : [];
      const discountShares = distributeDiscountAcrossRows(
        entryItems.map((it) => Number(it?.amount) || 0),
        en?.discountAmount || 0
      );

      return entryItems.map((it, idx) => {
        const key = String(it.material || "").toLowerCase();
        const snapshotCode = String(it.itemCode || "").trim();
        const snapshotGroup = String(it.itemGroup || "").trim();
        const master = snapshotCode && snapshotGroup ? null : itemByName.get(key) || itemByCode.get(key);
        const grossAmount = Number(it.amount) || 0;
        const rowDiscount = Number((discountShares[idx] || 0) / 100) || 0;
        const netAmount = +(grossAmount - rowDiscount).toFixed(2);
        return {
          id: `${en.id || idx}-${idx}`,
          billNo: en.billNo || "",
          saleDate: en.saleDate || "",
          customer: en.customer || "",
          paymentDone: !!en.paymentDone,
          paymentMode: normalizeBillPaymentMode(en.paymentType),
          item: it.material || "",
          itemCode: snapshotCode || master?.code || "",
          itemGroup: snapshotGroup || master?.itemGroup || "",
          qty: it.qty,
          rate: it.rate,
          grossAmount,
          discountAmount: +rowDiscount.toFixed(2),
          amount: netAmount,
        };
      });
    });
  }, [entries, itemByName, itemByCode]);

  const filtered = useMemo(() => {
    const itemFilter = filters.item.trim().toLowerCase();
    const codeFilter = filters.itemCode.trim().toLowerCase();
    const groupFilter = filters.itemGroup.trim().toLowerCase();
    return itemRows.filter((row) => {
      const d = row.saleDate ? toComparableYmd(row.saleDate) : "";
      if (filters.from && d < filters.from) return false;
      if (filters.to && d > filters.to) return false;
      if (filters.customer && !row.customer.toLowerCase().includes(filters.customer.toLowerCase())) return false;
      if (itemFilter && !row.item.toLowerCase().includes(itemFilter)) return false;
      if (codeFilter && !row.itemCode.toLowerCase().includes(codeFilter)) return false;
      if (groupFilter && !row.itemGroup.toLowerCase().includes(groupFilter)) return false;
      if (filters.payment !== "any") {
        if (filters.payment === "done" && !row.paymentDone) return false;
        if (filters.payment === "pending" && row.paymentDone) return false;
      }
      if (filters.paymentMode !== "any" && row.paymentMode !== filters.paymentMode) return false;
      return true;
    });
  }, [itemRows, filters]);

  const hasActiveFilters = useMemo(
    () =>
      Object.values(filters).some((value) => String(value || "").trim() !== "" && String(value) !== "any"),
    [filters]
  );

  const itemNameSummary = useMemo(() => {
    const map = new Map();
    for (const row of filtered) {
      const groupName = String(row.item || "").trim() || "Unnamed item";
      if (!map.has(groupName)) {
        map.set(groupName, {
          group: groupName,
          qty: 0,
          amount: 0,
          rows: 0,
        });
      }
      const bucket = map.get(groupName);
      bucket.qty += Number(row.qty) || 0;
      bucket.amount += Number(row.amount) || 0;
      bucket.rows += 1;
    }

    return Array.from(map.values())
      .map((row) => ({
        ...row,
        qty: +Number(row.qty || 0).toFixed(3),
        amount: +Number(row.amount || 0).toFixed(2),
      }))
      .sort((a, b) => String(a.group || "").localeCompare(String(b.group || ""), undefined, { sensitivity: "base" }));
  }, [filtered]);

  const filteredItemTotals = useMemo(() => {
    return itemNameSummary.reduce(
      (acc, row) => {
        acc.qty += Number(row.qty) || 0;
        acc.amount += Number(row.amount) || 0;
        return acc;
      },
      { qty: 0, amount: 0 }
    );
  }, [itemNameSummary]);

  const selectedFilterLabels = useMemo(() => {
    const labels = [];
    if (filters.from) labels.push(`From: ${formatDisplayDate(filters.from)}`);
    if (filters.to) labels.push(`To: ${formatDisplayDate(filters.to)}`);
    if (filters.customer.trim()) labels.push(`Customer: ${filters.customer.trim()}`);
    if (filters.item.trim()) labels.push(`Item: ${filters.item.trim()}`);
    if (filters.itemCode.trim()) labels.push(`Code: ${filters.itemCode.trim()}`);
    if (filters.itemGroup.trim()) labels.push(`Group: ${filters.itemGroup.trim()}`);
    if (filters.payment !== "any") labels.push(`Payment: ${filters.payment === "done" ? "Done" : "Pending"}`);
    if (filters.paymentMode !== "any") labels.push(`Payment Mode: ${filters.paymentMode}`);
    if (selectedSummaryItem) labels.push(`Selected Card: ${selectedSummaryItem}`);
    return labels;
  }, [filters, selectedSummaryItem]);

  useEffect(() => {
    setPage(1);
  }, [filters, selectedSummaryItem]);

  useEffect(() => {
    if (!selectedSummaryItem) return;
    if (!itemNameSummary.some((row) => row.group === selectedSummaryItem)) {
      setSelectedSummaryItem("");
    }
  }, [itemNameSummary, selectedSummaryItem]);

  const visibleTableRows = useMemo(() => {
    if (!selectedSummaryItem) return filtered;
    return filtered.filter((row) => (String(row.item || "").trim() || "Unnamed item") === selectedSummaryItem);
  }, [filtered, selectedSummaryItem]);

  const totalPages = useMemo(() => {
    const total = Math.ceil(visibleTableRows.length / pageSize);
    return total > 0 ? total : 1;
  }, [visibleTableRows.length, pageSize]);

  useEffect(() => {
    setPage((prev) => {
      if (prev < 1) return 1;
      if (prev > totalPages) return totalPages;
      return prev;
    });
  }, [totalPages]);

  const pagedFiltered = useMemo(() => {
    const start = (page - 1) * pageSize;
    return visibleTableRows.slice(start, start + pageSize);
  }, [visibleTableRows, page, pageSize]);

  const pageStartIndex = visibleTableRows.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEndIndex =
    visibleTableRows.length === 0
      ? 0
      : Math.min((page - 1) * pageSize + pagedFiltered.length, visibleTableRows.length);

  const exportData = () => {
    if (visibleTableRows.length === 0) {
      alert("No rows to export");
      return;
    }
    const rows = [...visibleTableRows]
      .sort((a, b) => {
        const itemCmp = String(a.item || "").localeCompare(String(b.item || ""), undefined, { sensitivity: "base" });
        if (itemCmp !== 0) return itemCmp;

        const codeCmp = String(a.itemCode || "").localeCompare(String(b.itemCode || ""), undefined, {
          sensitivity: "base",
        });
        if (codeCmp !== 0) return codeCmp;

        const dateCmp = toComparableYmd(a.saleDate).localeCompare(toComparableYmd(b.saleDate));
        if (dateCmp !== 0) return dateCmp;

        return String(a.billNo || "").localeCompare(String(b.billNo || ""), undefined, { numeric: true });
      })
      .map((row) => ({
      BillNo: row.billNo,
      Date: formatDisplayDate(row.saleDate),
      Customer: row.customer.replace(/\n/g, ", "),
      Item: row.item,
      ItemCode: row.itemCode,
      ItemGroup: row.itemGroup,
      Qty: row.qty,
      Rate: Number(row.rate || 0).toFixed(2),
      Amount: Number(row.amount || 0).toFixed(2),
      Payment: row.paymentDone ? "Done" : "Pending",
      PaymentMode: row.paymentMode,
      }));
    downloadCSV(rows, `sales-report-${Date.now()}.csv`);
  };

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">Sales Report</h2>
          <button className="btn-secondary" onClick={refresh} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
        <div className="inward-form" style={{ gap: "12px" }}>
          <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
            <div className="field">
              <label>From Date</label>
              <input
                type="date"
                className="input-field"
                value={filters.from}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>To Date</label>
              <input
                type="date"
                className="input-field"
                value={filters.to}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Customer</label>
              <input
                type="text"
                className="input-field"
                placeholder="Name / phone"
                value={filters.customer}
                onChange={(e) => setFilters((f) => ({ ...f, customer: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Item</label>
              <input
                type="text"
                className="input-field"
                placeholder="Item name"
                value={filters.item}
                onChange={(e) => setFilters((f) => ({ ...f, item: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Item Code</label>
              <input
                type="text"
                className="input-field"
                placeholder="Code"
                value={filters.itemCode}
                onChange={(e) => setFilters((f) => ({ ...f, itemCode: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Item Group</label>
              <input
                type="text"
                className="input-field"
                placeholder="Group"
                value={filters.itemGroup}
                onChange={(e) => setFilters((f) => ({ ...f, itemGroup: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Payment</label>
              <select
                className="input-field"
                value={filters.payment}
                onChange={(e) => setFilters((f) => ({ ...f, payment: e.target.value }))}
              >
                <option value="any">All</option>
                <option value="done">Done</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="field">
              <label>Payment Mode</label>
              <select
                className="input-field"
                value={filters.paymentMode}
                onChange={(e) => setFilters((f) => ({ ...f, paymentMode: e.target.value }))}
              >
                <option value="any">Any</option>
                {BILLING_PAYMENT_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-actions" style={{ justifyContent: "space-between" }}>
            <div>{filtered.length} results</div>
            <button type="button" className="btn-primary" onClick={exportData}>
              Export Excel (CSV)
            </button>
          </div>
        </div>
      </div>

      {hasActiveFilters && itemNameSummary.length > 0 ? (
        <div className="entries-card">
          <div
            className="card-header"
            style={{ borderBottom: "none", gap: 12, flexWrap: "wrap", alignItems: "center" }}
          >
            <h2 className="card-title">Item Name Summary</h2>
            {selectedFilterLabels.length > 0 ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: "1 1 420px" }}>
                {selectedFilterLabels.map((label) => (
                  <span
                    key={label}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "5px 10px",
                      borderRadius: 999,
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      color: "#1d4ed8",
                      fontSize: "0.78rem",
                    fontWeight: 700,
                  }}
                >
                  {label}
                </span>
                ))}
              </div>
            ) : null}
            <span className="entry-count">{itemNameSummary.length} items</span>
          </div>
          <div style={{ padding: "0 16px 16px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 280px))",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  borderRadius: 16,
                  border: "1px solid #bfdbfe",
                  background: "linear-gradient(145deg, #eff6ff, #dbeafe)",
                  boxShadow: "0 12px 24px rgba(59, 130, 246, 0.12)",
                  padding: "14px 16px",
                }}
              >
                <div style={{ fontSize: "0.76rem", color: "#1d4ed8", marginBottom: 6 }}>
                  Filter Total Qty
                </div>
                <div style={{ fontWeight: 900, fontSize: "1.2rem", color: "#1e3a8a" }}>
                  {Number(filteredItemTotals.qty || 0).toFixed(2)}
                </div>
              </div>
              <div
                style={{
                  borderRadius: 16,
                  border: "1px solid #bbf7d0",
                  background: "linear-gradient(145deg, #f0fdf4, #dcfce7)",
                  boxShadow: "0 12px 24px rgba(34, 197, 94, 0.12)",
                  padding: "14px 16px",
                }}
              >
                <div style={{ fontSize: "0.76rem", color: "#15803d", marginBottom: 6 }}>
                  Filter Total Amount
                </div>
                <div style={{ fontWeight: 900, fontSize: "1.2rem", color: "#166534" }}>
                  {Number(filteredItemTotals.amount || 0).toFixed(2)}
                </div>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              {itemNameSummary.map((group) => (
                <button
                  key={group.group}
                  type="button"
                  onClick={() =>
                    setSelectedSummaryItem((prev) => (prev === group.group ? "" : group.group))
                  }
                  style={{
                    borderRadius: 16,
                    border:
                      selectedSummaryItem === group.group
                        ? "2px solid #2563eb"
                        : "1px solid rgba(148, 163, 184, 0.24)",
                    background:
                      selectedSummaryItem === group.group
                        ? "linear-gradient(145deg, #dbeafe, #eff6ff)"
                        : "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(241,245,249,0.92))",
                    boxShadow:
                      selectedSummaryItem === group.group
                        ? "0 16px 28px rgba(37, 99, 235, 0.18)"
                        : "0 12px 24px rgba(15, 23, 42, 0.08)",
                    padding: "14px 16px",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "all 0.16s ease",
                  }}
                >
                  <div style={{ fontSize: "0.78rem", color: "#64748b", marginBottom: 6 }}>
                    Item Name
                  </div>
                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: "1rem",
                      color: "#0f172a",
                      marginBottom: 10,
                      wordBreak: "break-word",
                    }}
                  >
                    {group.group}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        borderRadius: 12,
                        padding: "10px 12px",
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      <div style={{ fontSize: "0.72rem", color: "#1d4ed8" }}>Total Qty</div>
                      <div style={{ fontWeight: 800, color: "#1e3a8a" }}>{group.qty.toFixed(2)}</div>
                    </div>
                    <div
                      style={{
                        borderRadius: 12,
                        padding: "10px 12px",
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      <div style={{ fontSize: "0.72rem", color: "#15803d" }}>Total Amount</div>
                      <div style={{ fontWeight: 800, color: "#166534" }}>
                        {Number(group.amount || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 10, fontSize: "0.76rem", color: "#475569" }}>
                    {group.rows} item row{group.rows === 1 ? "" : "s"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div className="entries-card">
        <div className="entries-table-container">
          <table className="entries-table" style={{ minWidth: "1100px" }}>
            <thead>
              <tr>
                <th>Bill No</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Item Codes</th>
                <th>Item Groups</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Rate</th>
                <th className="text-right">Amount</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              {pagedFiltered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty-state">
                    No data
                  </td>
                </tr>
              ) : (
                pagedFiltered.map((en) => (
                  <tr key={en.id}>
                    <td>{en.billNo || ""}</td>
                    <td>{formatDisplayDate(en.saleDate)}</td>
                    <td style={{ whiteSpace: "pre-wrap" }}>{en.customer}</td>
                    <td>{en.item}</td>
                    <td>{en.itemCode}</td>
                    <td>{en.itemGroup}</td>
                    <td className="text-right">{en.qty}</td>
                    <td className="text-right">{Number(en.rate || 0).toFixed(2)}</td>
                    <td className="text-right">{Number(en.amount || 0).toFixed(2)}</td>
                    <td>{en.paymentDone ? "Done" : "Pending"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="form-actions" style={{ justifyContent: "space-between" }}>
          <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
            {`Showing ${pageStartIndex}-${pageEndIndex} of ${visibleTableRows.length} rows`}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {selectedSummaryItem ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setSelectedSummaryItem("")}
                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
              >
                Clear Card Filter
              </button>
            ) : null}
            <select
              className="input-field compact"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value) || 10);
                setPage(1);
              }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>
            <button
              type="button"
              className="btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
            >
              Prev
            </button>
            <span style={{ minWidth: 90, textAlign: "center", color: "#475569", fontSize: "0.85rem" }}>
              Page {page} / {totalPages}
            </span>
            <button
              type="button"
              className="btn-secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
